import {damsFetch} from "../app/damsFetch";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {shareRefusalMessage} from "../share-model/shareRefusal";
import {toFolderIdNumber} from "./toFolderIdNumber";

const isResultList = (data) => Array.isArray(data);

/**
 * A hook that asks the server to mail a share notification (POST /share/notify), and shows a snackbar with the
 * outcome. The server writes the mail; the client sends only the target, the recipients and the personal message.
 *
 * @returns {{sendShareNotification: function}}
 */
export const useEmail = () => {
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation("dams.email", {useSuspense: false});

    const showDefaultError = (status) => enqueueSnackbar(
        shareRefusalMessage(t, status)
        ?? t("shareSnackDefaultErrorMessage", "Utsendelse av e-mail feilet, kontakt system administrator."),
        {variant: 'error'});

    const showStatusReport = (data) => {
        const failed = data.filter((r) => r.success === false).map((r) => r.recipient);
        if (failed.length > 0) {
            enqueueSnackbar(`${t("shareSnackErrorMessage", "Mailutsendelse feilet til")}: ${failed}`,
                {variant: 'error'});
        } else {
            enqueueSnackbar(`${t("shareSnackSuccessMessage", "Mail sendt til")}: ${data.map((r) => r.recipient)}`,
                {variant: 'success'});
        }
    };

    /**
     * Asks the server to mail the recipients about a shared document or folder.
     *
     * @param {Object} share
     * @param {string} [share.objectUuid] - The shared document. Either this or folderId.
     * @param {number|string} [share.folderId] - The shared folder.
     * @param {string[]} share.recipients - E-mail addresses.
     * @param {string} [share.message] - The sender's personal message.
     * @returns {Promise<boolean>} True when the server answered 200 (also when single recipients failed, which is
     * shown), false when the request failed or the answer was not understood.
     */
    const sendShareNotification = async ({objectUuid, folderId, recipients, message}) => {
        if (!recipients || recipients.length === 0) {
            return false;
        }
        const target = objectUuid ? {object_uuid: objectUuid} : {folder_id: toFolderIdNumber(folderId)};
        if (target.folder_id === null) {
            showDefaultError();
            return false;
        }
        try {
            const data = await damsFetch("/share/notify", {
                method: "POST",
                body: JSON.stringify({...target, recipient_emails: recipients, email_message: message ?? ''}),
            });
            if (!isResultList(data)) {
                showDefaultError();
                return false;
            }
            showStatusReport(data);
            return true;
        } catch (status) {
            showDefaultError(status);
            return false;
        }
    };

    return {sendShareNotification};
};
